import { Location, NavigateFunction } from "react-router-dom";
import { AvisConstants } from "../data/avisConstants";
import { AvisRoute } from "../data/avisRoutes";
import { RightsHolderInfoViewModel } from "../generated/data-contracts";
import { IUrlParamsAuthors, RightsHolderType } from "../models";
import { AvisUtils, ErrorUtils, api } from "./";

export class AuthorsUrls {
  static readonly rightsHoldersPortal = `/${AvisConstants.url_part_portal_authors}`;
  static readonly rightsHoldersPortalHome = `${AuthorsUrls.rightsHoldersPortal}/${AvisConstants.url_rights_holder_id_param}/${AvisConstants.url_rights_holder_type_param}`;
  static readonly rightsHoldersNames = `${AuthorsUrls.rightsHoldersPortalHome}/names`;
  static readonly rightsHoldersBands = `${AuthorsUrls.rightsHoldersPortalHome}/bands`;
  static readonly rightsHoldersContacts = `${AuthorsUrls.rightsHoldersPortalHome}/contacts`;
  static readonly rightsHoldersLinkedPersons = `${AuthorsUrls.rightsHoldersPortalHome}/linked-persons`;
  static readonly rightsHoldersDelegatedRights = `${AuthorsUrls.rightsHoldersPortalHome}/delegated-rights`;
  static readonly rightsHoldersBillingData = `${AuthorsUrls.rightsHoldersPortalHome}/billing-data`;
  static readonly rightsHoldersMemberStatus = `${AuthorsUrls.rightsHoldersPortalHome}/member-status`;
  static readonly rightsHoldersReports = `${AuthorsUrls.rightsHoldersPortalHome}/reports`;
  static readonly rightsHoldersCreations = `${AuthorsUrls.rightsHoldersPortalHome}/${AvisConstants.url_part_creations}`;
  static readonly rightsHoldersCreationView = `${AuthorsUrls.rightsHoldersPortalHome}/${AvisConstants.url_part_creations}/${AvisConstants.url_creation_type_param}/${AvisConstants.url_id_param}`;
  static readonly rightsHoldersCreationViewNew = `${AuthorsUrls.rightsHoldersPortalHome}/${AvisConstants.url_part_creations}/new`;
  static readonly rightsHoldersCreationUnidentifiedList = `${AuthorsUrls.rightsHoldersPortalHome}/${AvisConstants.url_part_unidentified}`;
  static readonly rightsHoldersCreationUnidentifiedView = `${AuthorsUrls.rightsHoldersCreationUnidentifiedList}/${AvisConstants.url_id_param}`;
}

export class AuthorsUtils {
  static getRightsHolderFullName(
    rightsHolder?: RightsHolderInfoViewModel,
  ): string {
    if (!rightsHolder) return "";

    return rightsHolder.firstName
      ? `${rightsHolder.firstName} ${rightsHolder.lastName}`
      : rightsHolder.lastName ?? "";
  }
  static getUrlWithParams(
    path: string | undefined,
    urlParams: IUrlParamsAuthors,
  ): string {
    if (!urlParams.rightsHolderId || !urlParams.rightsHolderTypeCode || !path)
      return path ?? `/${AvisConstants.url_part_portal_authors}`;

    let result = path;

    result = result.replace(
      AvisConstants.url_rights_holder_id_param,
      btoa(urlParams.rightsHolderId.toString()),
    );

    result = result.replace(
      AvisConstants.url_rights_holder_type_param,
      urlParams.rightsHolderTypeCode.toString(),
    );

    if (urlParams.creationType) {
      result = result.replace(
        AvisConstants.url_creation_type_param,
        urlParams.creationType,
      );

      if (urlParams.creationId) {
        result = result.replace(
          AvisConstants.url_id_param,
          btoa(urlParams.creationId.toString()),
        );
      }
    }

    if (urlParams.unidentifiedCreationProcessId) {
      result = result.replace(
        AvisConstants.url_id_param,
        btoa(urlParams.unidentifiedCreationProcessId.toString()),
      );
    }

    return result;
  }
  static reloadCurrentUrlWithParams(
    rightsHolder?: RightsHolderInfoViewModel,
    navigate?: NavigateFunction,
  ) {
    const route = AvisUtils.getRouteByUrl(location.pathname);

    if (!route) return;

    const urlParams = AuthorsUtils.getUrlParams(rightsHolder);

    const path = AuthorsUtils.getUrlWithParams(route.path, urlParams);

    if (path === location.pathname) return;

    navigate ? navigate(path) : location.assign(path);
  }

  static getRightsHolderFromUrl(rightsHolders?: RightsHolderInfoViewModel[]) {
    if (!rightsHolders) return null;

    const pathname = location.pathname.slice(1);

    const pathParts = pathname.split("/");

    if (pathParts.length < 2) return null;

    return rightsHolders.find(
      (rh) =>
        rh.rightsHolderId?.toString() ===
          AvisUtils.atobParam("rightsHolderId", pathParts[1]) &&
        rh.rightsHolderTypeCode === pathParts[2],
    );
  }

  static getUrlParams(
    rightsHolder?: RightsHolderInfoViewModel,
  ): IUrlParamsAuthors {
    const urlParams: IUrlParamsAuthors = {};
    const pathname = location.pathname.slice(1);
    const pathParts = pathname.split("/");

    if (pathParts.length < 3 || !rightsHolder) return urlParams;

    urlParams.rightsHolderId = parseInt(
      AvisUtils.atobParam("rightsHolderId", pathParts[1]),
    );
    urlParams.rightsHolderTypeCode = pathParts[2];

    if (urlParams.rightsHolderId != rightsHolder.rightsHolderId) {
      urlParams.rightsHolderId = rightsHolder.rightsHolderId;
      urlParams.rightsHolderTypeCode = rightsHolder.rightsHolderTypeCode;
    }
    if (pathParts.indexOf(AvisConstants.url_part_unidentified) == 3) {
      if (pathParts.length >= 5) {
        urlParams.unidentifiedCreationProcessId = parseInt(
          AvisUtils.atobParam("unidentifiedProcessId", pathParts[4]),
        );
      }
    } else if (
      pathParts.indexOf(AvisConstants.url_part_creations) == 3 &&
      pathParts.length >= 6
    ) {
      urlParams.creationType = pathParts[4];
      urlParams.creationId = parseInt(
        AvisUtils.atobParam("creationId", pathParts[5]),
      );
    }

    return urlParams;
  }

  static isValidRightHolder(rightsHolder?: RightsHolderInfoViewModel): boolean {
    if (rightsHolder?.rightsHolderId) return true;

    ErrorUtils.showErrorMessage();

    return false;
  }

  static getStartPagePath(rightsHolder?: RightsHolderInfoViewModel): string {
    return AuthorsUtils.getRightsHolderPagePath(rightsHolder);
  }

  static getRightsHolderPagePath(
    rightsHolder?: RightsHolderInfoViewModel,
    page?: string,
  ): string {
    let result = `/${AvisConstants.url_part_portal_authors}`;

    if (!rightsHolder?.rightsHolderId || !rightsHolder.rightsHolderTypeCode) {
      return result;
    }

    const pagePart = page ? `/${page}` : "";

    result += `/${btoa(rightsHolder.rightsHolderId.toString())}/${
      rightsHolder.rightsHolderTypeCode
    }${pagePart}`;

    return result;
  }
  static isAllowedLocation(
    location: Location<any>,
    ctxRightsHolder?: RightsHolderInfoViewModel,
  ): boolean {
    const avisRoute = AvisUtils.getRouteByUrl(location.pathname);

    return this.isAllowedRoute(avisRoute, ctxRightsHolder);
  }

  static isAllowedRouteByUrl(
    url?: string,
    ctxRightsHolder?: RightsHolderInfoViewModel,
  ): boolean {
    if (!url) return false;

    const avisRoute = AvisUtils.getRouteByUrl(url);

    return this.isAllowedRoute(avisRoute, ctxRightsHolder);
  }

  static isAllowedRoute(
    route?: AvisRoute,
    ctxRightsHolder?: RightsHolderInfoViewModel,
  ): boolean {
    if (route === undefined || !ctxRightsHolder) return false;

    if (route.allowedRightsHolderTypes === undefined) return true;

    return (
      route.allowedRightsHolderTypes.indexOf(
        ctxRightsHolder.rightsHolderTypeCode as RightsHolderType,
      ) !== -1
    );
  }
  static readonly handleFoundOrCreatedCreation = (
    rightsHolder: RightsHolderInfoViewModel,
    unidentifiedCreationProcessId: number,
    comment: string | undefined,
    creationId: number,
    created: boolean,
  ) => {
    return api.rightsHoldersPostUnidentifiedCreationStatus(
      rightsHolder.rightsHolderId,
      unidentifiedCreationProcessId,
      {
        belongsToRightsHolder: true,
        identifiedCreationId: creationId,
        isNewCreation: created,
        notes: comment,
      },
    );
  };
}
